// Robot mail: the pure rules behind the FYI tab's "Clear all" and its
// "looks automated" suggestions. See CODE_MAP.md "FYI lane for robot mail".
//
// Pure on purpose (no React, no Supabase): tests/robot-mail.test.ts.

/** The one automation rule that keeps robot mail out of Slack (migration 080).
 *  The database keeps its sender list in step with the flagged contacts, so the
 *  Automations page shows it as managed and doesn't offer Edit / Delete. */
export const ROBOT_MAIL_RULE_ID = '5afe0000-0000-4000-8000-00000000f711';

export function isRobotMailRule(rule: { id: string }): boolean {
  return rule.id === ROBOT_MAIL_RULE_ID;
}

const CLOSED = new Set(['resolved', 'closed']);

export interface ClearableTicket {
  id: string;
  status?: string | null;
  assignee_slack_id?: string | null;
}

export interface ClearPlan {
  /** What "Clear all" will resolve: every listed ticket that is still open. */
  ids: string[];
  /** How many of those are assigned to a teammate (not me, not nobody). */
  assignedToOthers: number;
}

export function planClearAll(tickets: readonly ClearableTicket[], mySlackId: string | null): ClearPlan {
  const open = tickets.filter((t) => !CLOSED.has(t.status ?? ''));
  return {
    ids: open.map((t) => t.id),
    assignedToOthers: open.filter((t) => !!t.assignee_slack_id && t.assignee_slack_id !== mySlackId).length,
  };
}

/** The confirmation's wording. `complete` is false when the view is longer than
 *  the search can page through, so the button must say some will remain. */
export function clearAllCopy(
  plan: ClearPlan,
  complete: boolean,
): { title: string; description: string; confirmLabel: string } {
  const n = plan.ids.length;
  const s = n === 1 ? '' : 's';
  const parts = ['They are marked resolved and leave the FYI tab.'];
  if (plan.assignedToOthers > 0) {
    const k = plan.assignedToOthers;
    parts.push(`${k} ${k === 1 ? 'is' : 'are'} assigned to a teammate.`);
  }
  if (!complete) {
    parts.push('This view is longer than can be fetched at once, so some will remain: run Clear all again afterwards.');
  }
  parts.push('A resolved ticket can be reopened.');
  return {
    title: `Resolve ${n} automated email${s}?`,
    description: parts.join(' '),
    confirmLabel: `Resolve ${n}`,
  };
}

/** Suggestions the person hasn't waved away with "Not a robot". */
export function visibleSuggestions<T extends { email: string }>(
  list: readonly T[] | undefined,
  dismissed: ReadonlySet<string>,
): T[] {
  return (list ?? []).filter((s) => !dismissed.has(s.email.toLowerCase()));
}

const DISMISSED_KEY = 'pa-fyi-dismissed';

/** Senders this browser's user said are not robots (a per-person convenience). */
export function readDismissed(storage: Pick<Storage, 'getItem'> | null): Set<string> {
  try {
    const raw = storage?.getItem(DISMISSED_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function writeDismissed(storage: Pick<Storage, 'setItem'> | null, set: ReadonlySet<string>): void {
  try {
    storage?.setItem(DISMISSED_KEY, JSON.stringify([...set].slice(-500)));
  } catch {
    /* private window / blocked storage: the choice just isn't remembered */
  }
}
